import { api } from '@/lib/axios';
import type { Decompte, ModeGasoilEntrante, MotifPerte } from '@/services/decomptesLocation';

/** Un loueur = le propriétaire de plusieurs engins (comme un sous-traitant). */
export interface LoueurDTO {
  id: string;
  raison_sociale: string;
  telephone: string | null;
  nb_engins: number;
  nb_decomptes_ouverts: number;
}
export interface EnginACompleter {
  id: string;
  code: string;
  designation: string;
  manque_loueur: boolean;
  manque_tarif: boolean;
}
export interface GrilleJour {
  date: string;
  journees: string;
  heures_sup: string;
  heures_perdues: string;
  heure_debut: string | null;
  heure_fin: string | null;
  motif_perte: MotifPerte | null;
  gasoil_litres: string;     // gasoil du jour, tous pleins confondus
  gasoil_montant: string;    // ce qu'il représente en argent (0 si droit « tarifs » absent)
  montant: string;
}
export interface GrilleLigne {
  engin_id: string;
  engin_code: string;
  engin_libelle: string;
  categorie: string | null;
  marque: string | null;
  modele: string | null;
  immatriculation: string | null;
  tarif_jour: string;
  decompte_id: string | null;      // null = engin pas encore affecté à un chantier
  decompte_reference: string | null;
  chantier_id: string | null;
  chantier_libelle: string | null;
  statut: string | null;
  modifiable: boolean;
  mode_gasoil: ModeGasoilEntrante | null;
  chauffeur_nom: string | null;
  jours: GrilleJour[];
  total_jours: string;
  total_heures_sup: string;
  total_brut: string;
  gasoil_litres: string;
  gasoil_cout: string;       // argent représenté par ce gasoil (0 si droit « tarifs » absent)
  alerte_surconso: boolean;
}
export interface AFacturer {
  chantier_id: string;
  chantier_libelle: string;
  nb_decomptes: number;
  net: string;
}
export interface GrilleLoueur {
  loueur_id: string;
  raison_sociale: string;
  date_from: string;
  date_to: string;
  lignes: GrilleLigne[];
  total_jours: string;
  total_brut: string;
  a_facturer: AFacturer[];
  montants_masques: boolean;
}

export async function listLoueurs(): Promise<LoueurDTO[]> {
  const { data } = await api.get<LoueurDTO[]>('/locations-entrantes/loueurs');
  return data;
}
export async function listEnginsACompleter(): Promise<EnginACompleter[]> {
  const { data } = await api.get<EnginACompleter[]>('/locations-entrantes/a-completer');
  return data;
}
export async function getGrilleLoueur(loueurId: string, dateFrom: string, dateTo: string): Promise<GrilleLoueur> {
  const { data } = await api.get<GrilleLoueur>('/locations-entrantes/grille', {
    params: { loueur_id: loueurId, date_from: dateFrom, date_to: dateTo },
  });
  return data;
}

export interface PointageEntrantPayload {
  engin_id: string;
  chantier_id: string;
  date: string;
  journees: string;
  heures_sup?: string;
  /** Horaires précis : le serveur calcule journées, h. sup et déduction (journée 08:00 → 16:00). */
  heure_debut?: string;
  heure_fin?: string;
  motif_perte?: MotifPerte;
  /** Gasoil du JOUR (total) : litres, et montant — calculé par le serveur s'il est omis ou nul. */
  gasoil_litres?: string;
  gasoil_avance?: string;
  /** Réglages du décompte, utilisés seulement s'il faut le créer (premier pointage). */
  decompte_mode_gasoil?: ModeGasoilEntrante;
  decompte_chauffeur_nom?: string | null;
}
/** Pointe UNE journée d'UN engin ; le décompte (engin × chantier) est créé au premier pointage. */
export async function pointerEntrant(payload: PointageEntrantPayload): Promise<Decompte> {
  const { data } = await api.put<Decompte>('/locations-entrantes/pointage', payload);
  return data;
}
/** Affecte un engin à un chantier (ouvre son décompte) sans pointer de journée. */
export async function ouvrirDecompteEngin(payload: {
  engin_id: string; chantier_id: string; mode_gasoil?: ModeGasoilEntrante; chauffeur_nom?: string | null;
}): Promise<Decompte> {
  const { data } = await api.post<Decompte>('/locations-entrantes/ouvrir', payload);
  return data;
}
/** UNE facture fournisseur pour ce loueur sur ce chantier, tous ses engins cumulés. */
export async function facturerLoueur(loueurId: string, chantierId: string): Promise<{
  facture_id: string; facture_reference: string | null; net: string; nb_decomptes: number;
}> {
  const { data } = await api.post('/locations-entrantes/facturer', { loueur_id: loueurId, chantier_id: chantierId });
  return data;
}

export interface ModifierEnginPayload {
  designation?: string;
  categorie?: string;
  marque?: string;
  modele?: string;
  immatriculation?: string;
  loueur_id?: string;
  /** Prix par jour : exige le droit « tarifs » et s'applique aux décomptes encore ouverts. */
  cout_location_jour?: number;
  /** Décompte en cours de l'engin : chantier, chauffeur et gasoil. */
  decompte_id?: string;
  chantier_id?: string;
  chauffeur_nom?: string;
  mode_gasoil?: ModeGasoilEntrante;
}
/** Corrige une machine louée (faute de frappe, propriétaire, prix) et son décompte en cours. */
export async function modifierEnginLoue(enginId: string, payload: ModifierEnginPayload): Promise<void> {
  await api.put(`/locations-entrantes/engins/${enginId}`, payload);
}
